import { AxiosError, HttpStatusCode } from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fAPIDate } from 'src/utils/format-time';

import { ChequeStatus, IChequeItem, IChequeListItem, IChequeReceived } from 'src/types/cheque';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  cheque: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IChequeListItem[];
  };
  total: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: { totalAmount: number | null };
  };
  item: IChequeItem | null;
  received: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: IChequeReceived[];
  };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  cheque: {
    count: 0,
    page: 1,
    pageSize: 10,
    pageCount: 0,
    items: [],
  },
  total: {
    status: 'idle',
    error: undefined,
    data: { totalAmount: null },
  },
  item: null,
  received: {
    status: 'idle',
    error: undefined,
    data: [],
  },
};

const cheque = createSlice({
  name: 'cheque',
  initialState,
  reducers: {
    removeChequeItem: (state, action) => {
      const { itemId } = action.payload;

      const index = state.cheque.items.findIndex((item) => item._id === itemId);

      if (index > -1) {
        action.payload.index = index; // Store the index for rollback
        state.cheque.items = state.cheque.items.filter((item) => item._id !== itemId);
      }
    },
    rollbackChequeItemDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.cheque.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCheques.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getCheques.fulfilled, (state, action) => {
        state.status = 'success';
        state.cheque = action.payload;
      })
      .addCase(getCheques.rejected, (state, action) => {
        state.status = 'failure';
        state.error = action.error.message;
      })

      .addCase(getChequeReceived.pending, (state) => {
        state.received.status = 'loading';
      })
      .addCase(getChequeReceived.fulfilled, (state, action) => {
        state.received.status = 'success';
        state.received.data = action.payload;
      })
      .addCase(getChequeReceived.rejected, (state, action) => {
        state.received.status = 'failure';
        state.received.error = action.error.message;
      })

      .addCase(getChequeTotal.pending, (state) => {
        state.total.status = 'loading';
      })
      .addCase(getChequeTotal.fulfilled, (state, action) => {
        state.total.status = 'success';
        state.total.data = action.payload;
      })
      .addCase(getChequeTotal.rejected, (state, action) => {
        state.total.status = 'failure';
        state.total.error = action.error.message;
      })

      .addCase(getChequeByID.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getChequeByID.fulfilled, (state, action) => {
        state.status = 'success';
        state.item = action.payload;
      })
      .addCase(getChequeByID.rejected, (state, action) => {
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default cheque.reducer;

export const { removeChequeItem, rollbackChequeItemDeletion } = cheque.actions;

export const getCheques = createAsyncThunk(
  'cheque/getCheques',
  async (
    params: {
      page: number;
      limit: number;
      generalSearch?: string | null;
      customer?: string | null;
      chequeNumber?: string | null;
      receivedVia?: string | null;
      holder?: string | null;
      chequeDate?: Date | null;
      startDate?: Date | null;
      endDate?: Date | null;
      receivedDate?: Date | null;
      paidDate?: Date | null;
      bank?: string | null;
      status?: string | null; // TODO: Enum
      type?: string | null; // TODO: Enum
      sortBy?: string | null;
      sort?: 'asc' | 'desc' | null;
    } = {
      page: 1,
      limit: 10,
      generalSearch: null,
      customer: null,
      chequeNumber: null,
      receivedVia: null,
      bank: null,
      chequeDate: null,
      startDate: null,
      endDate: null,
      receivedDate: null,
      paidDate: null,
      holder: null,
      status: null,
      type: null,
      sort: 'desc',
      sortBy: 'updatedAt',
    }
  ) => {
    const {
      page,
      limit,
      generalSearch,
      sortBy,
      sort,
      bank,
      chequeDate,
      chequeNumber,
      customer,
      holder,
      receivedVia,
      status,
      type,
      startDate,
      endDate,
      receivedDate,
      paidDate,
    } = params;

    try {
      const response = await axios.get(
        `cheque?page=${page + 1}&limit=${limit}&generalSearch=${generalSearch ?? ''}&customer=${
          customer ?? ''
        }&chequeNumber=${chequeNumber ?? ''}&receivedVia=${receivedVia ?? ''}&holder=${
          holder ?? ''
        }&chequeDate=${chequeDate ?? ''}&receivedDate=${receivedDate ?? ''}&paidDate=${
          paidDate ?? ''
        }&bank=${bank ?? ''}&status=${status ?? ''}&type=${type ?? ''}&sort=${
          sort ?? ''
        }&sortBy=${sortBy}&startDate=${fAPIDate(startDate)}&endDate=${fAPIDate(endDate)}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getChequeReceived = createAsyncThunk('cheque/getChequeReceived', async () => {
  try {
    const response = await axios.get(`cheque/received`);

    return response.data.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const getChequeTotal = createAsyncThunk(
  'cheque/getTotal',
  async (
    params: {
      generalSearch?: string | null;
      customer?: string | null;
      chequeNumber?: string | null;
      receivedVia?: string | null;
      holder?: string | null;
      chequeDate?: Date | null;
      startDate?: Date | null;
      endDate?: Date | null;
      receivedDate?: Date | null;
      paidDate?: Date | null;
      bank?: string | null;
      status?: string | null;
      type?: string | null;
    } = {
      generalSearch: null,
      customer: null,
      chequeNumber: null,
      receivedVia: null,
      bank: null,
      chequeDate: null,
      startDate: null,
      endDate: null,
      holder: null,
      status: null,
      type: null,
      receivedDate: null,
      paidDate: null,
    }
  ) => {
    const {
      generalSearch,
      bank,
      chequeDate,
      chequeNumber,
      customer,
      holder,
      receivedVia,
      status,
      type,
      startDate,
      endDate,
      receivedDate,
      paidDate,
    } = params;

    try {
      const response = await axios.get(
        `cheque/total?generalSearch=${generalSearch ?? ''}&customer=${
          customer ?? ''
        }&chequeNumber=${chequeNumber ?? ''}&receivedVia=${receivedVia ?? ''}&holder=${
          holder ?? ''
        }&chequeDate=${chequeDate ?? ''}&receivedDate=${receivedDate ?? ''}&paidDate=${
          paidDate ?? ''
        }&bank=${bank ?? ''}&status=${status ?? ''}&type=${type ?? ''}&startDate=${fAPIDate(
          startDate
        )}&endDate=${fAPIDate(endDate)}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getChequeByID = createAsyncThunk(
  'cheque/getChequeByID',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`cheque/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteCheque = createAsyncThunk(
  'cheque/deleteCheque',
  async (params: { item: IChequeListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`cheque/${item._id}`);
      return response.data.data;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === HttpStatusCode.FailedDependency) {
        dispatch(rollbackChequeItemDeletion({ item, index }));
        throw new AxiosError(err.response.data.message);
      }

      throw err;
    }
  }
);

export interface TCreateChequeItem
  extends Pick<
    IChequeItem,
    'customer' | 'receivedVia' | 'chequeNumber' | 'amountInCents' | 'bank' | 'status'
  > {
  chequeDate: Date;
  receivedDate: Date;
  status: ChequeStatus;
  invoice?: string | null;
}

export interface TStatusUpdateChequeItem {
  status: ChequeStatus;
  holder?: string | null;
  date: Date;
}

export const createCheque = createAsyncThunk(
  'cheque/createCheque',
  async (params: { newCheque: TCreateChequeItem }) => {
    const { newCheque } = params;
    const response = await axios.post(`cheque`, newCheque);
    return response.data.data;
  }
);

export const updateCheque = createAsyncThunk(
  'cheque/updateCheque',
  async (params: { chequeID: string; updatingCheque: TCreateChequeItem }) => {
    const { updatingCheque, chequeID } = params;
    const response = await axios.patch(`cheque/${chequeID}`, updatingCheque);
    return response.data.data;
  }
);

export const updateChequeStatus = createAsyncThunk(
  'cheque/updateChequeStatus',
  async (params: { chequeID: string; updatingCheque: TStatusUpdateChequeItem }) => {
    const { updatingCheque, chequeID } = params;
    const response = await axios.patch(`cheque/${chequeID}/status`, updatingCheque);
    return response.data.data;
  }
);
